import clsx from "clsx";
import { LucideProps } from "lucide-react";
import React from "react";

import dimensionTokens from "../../tokens/dimension.value.tokens.json";
import styles from "./index.module.css";
import { ScrollArea } from "components/ScrollArea/ScrollArea";
const ICON_SIZE = parseInt(dimensionTokens.component.icon.md.$value);

interface TabItemProps {
  /** Content to display inside the tab */
  children: React.ReactNode;
  /** Whether the tab is currently active */
  isActive?: boolean;
  /** Optional icon to display before the tab content */
  icon?: React.ReactElement<LucideProps>;
  /** Function called when the tab is clicked */
  onClick?: () => void;
  /** Additional CSS class names */
  className?: string;
}

const TabItem: React.FC<TabItemProps> = ({
  children,
  isActive = false,
  icon,
  onClick,
  className,
  ...rest
}) => {
  const classes = clsx(
    styles.tab,
    {
      [styles.isActive]: isActive,
    },
    className
  );

  const clonedIcon = icon && React.cloneElement(icon, { size: ICON_SIZE });

  return (
    <button className={classes} onClick={onClick} type="button" {...rest}>
      <span className={styles.tabContent}>
        {clonedIcon && <span>{clonedIcon}</span>}
        {children}
      </span>
    </button>
  );
};

interface TabsProps {
  /** `TabItem` components to display as tabs */
  children: React.ReactNode;
  /** Additional CSS class names */
  className?: string;
  showBorder?: boolean;
}

const Tabs: React.FC<TabsProps> = ({
  children,
  className,
  showBorder = true,
  ...rest
}) => {
  const classes = clsx(styles.wrapper, className);
  const scrollAreaClasses = clsx(styles.scrollArea, {
    [styles.bottomBorder]: showBorder,
  });

  return (
    <ScrollArea
      className={classes}
      contentClassName={scrollAreaClasses}
      orientation="horizontal"
      hideScrollBar
    >
      <ol className={styles.tabs} role="tablist" {...rest}>
        {React.Children.map(children, (child, index) => (
          <li key={index} className={styles.tabItem}>
            {child}
          </li>
        ))}
      </ol>
    </ScrollArea>
  );
};

export { TabItem, Tabs };
